// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { navigate } from "@reach/router";
import { Button, Divider, Form, message, Modal, Popconfirm } from "antd";
import { QuestionCircleOutlined, SendOutlined } from "@ant-design/icons";
import {
  formItemBlockLayout,
  LINKS_NUMBER_BY_NEWS_THEME
} from "../../../../utils/Constant";
import { SaveOutlined, StopOutlined } from "@ant-design/icons/lib";
import { addNewsTheme } from "../../../../state/newsTheme/NewsThemeEvents";
import NewsThemeInputFormSection from "../details/NewsThemeInputFormSection";
import NewsThemeColorFormSection from "../details/NewsThemeColorFormSection";
import { requestCreateNewsTheme } from "../../../../state/newsTheme/NewsThemeEffects";
import NewsThemeContactFormSection from "../details/NewsThemeContactFormSection";
import NewsThemeLinksFormSection from "../details/NewsThemeLinksFormSection";
import { NewsThemeWriteRequestDTO } from "../../../../api/backend-openapi";
import { NewsThemeLinkDTO } from "../../../types/NewsThemeTypes";
import { Field } from "../../../types/NewsTypes";
import { validate } from "../../../../validator/NewsThemeValidator";
import { ValidatorResult } from "../../../types/Types";
import { displayError } from "../../../../utils/displayError";

const { confirm } = Modal;

const AddNewsThemeForm: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const [buttonLoading, setButtonLoading] = useState<boolean>(false);

  const [color, setColor] = useState<string>();

  const defaultFieldsValues: Field[] = [
    {
      name: ["publish"],
      value: "true"
    }
  ];

  const confirmationPopup = async (publish: boolean) => {
    return new Promise<boolean>(resolve => {
      confirm({
        title: t("global.confirm"),
        content: publish ? t("news_theme.publish") : t("news_theme.save"),
        okText: t("global.yes"),
        cancelText: t("global.no"),
        centered: true,
        onOk() {
          resolve(true);
        },
        onCancel() {
          resolve(false);
        }
      });
    });
  };

  useEffect(() => {
    return requestCreateNewsTheme.done.watch(({ result }) => {
      setButtonLoading(false);
      if (result.ok && result.data) {
        addNewsTheme(result.data);
        message.success(t("add_news_theme.success"));
        navigate("/newsThemes");
      } else {
        displayError(result.errorMessage || "Error!", false);
      }
    });
  });

  const handleSubmit = (
    values: { [key: string]: string | undefined },
    publish: boolean
  ) => {
    const validationResult: ValidatorResult = validate({ ...values, color });
    if (!validationResult.validate) {
      displayError(validationResult.message);
    } else {
      confirmationPopup(publish).then((confirmed: boolean) => {
        if (confirmed) {
          setButtonLoading(true);
          const links: NewsThemeLinkDTO[] = [];
          let i: 0 | 1 | 2;
          for (i = 0; i < LINKS_NUMBER_BY_NEWS_THEME; i++) {
            links.push({
              title: values[`link${i}Title`] || "",
              detail: values[`link${i}Detail`] || "",
              url: values[`link${i}Url`] || ""
            });
          }
          const newsThemeToAdd: NewsThemeWriteRequestDTO = {
            name: values.name || "",
            published: publish,
            contactMail: values.contactMail,
            contactLabel: values.contactLabel,
            color,
            links,
            branch: values.branch || ""
          };
          requestCreateNewsTheme(newsThemeToAdd);
          setButtonLoading(true);
        }
      });
    }
  };

  const handleSave = (values: any) => {
    handleSubmit(values, false);
  };

  const handlePublish = () => {
    form
      .validateFields()
      .then(values => {
        handleSubmit(values, true);
      })
      .catch(() => {
        displayError("global.on_finish_failed");
      });
  };

  const onFinishFailed = ({ errorFields }: any) => {
    displayError("global.on_finish_failed");
    form.scrollToField(errorFields[0].name);
  };

  return (
    <Form
      {...formItemBlockLayout}
      onFinish={handleSave}
      onFinishFailed={onFinishFailed}
      fields={defaultFieldsValues}
      form={form}
    >
      <div className="form-first-item-sticky-top-container">
        <div className="form-first-item-sticky-top-content">
          <div className="d-flex align-items-center justify-content-between flex-wrap px-3">
            <Divider orientation="left">
              <h3 className="text-secondary mb-0">{t("news_theme.actions")}</h3>
            </Divider>
            <div className="my-3">
              <Popconfirm
                title={t("add_news_theme.cancel_confirmation")}
                okText={t("global.yes")}
                cancelText={t("global.no")}
                onConfirm={() => {
                  navigate("/newsThemes");
                  form.resetFields();
                }}
                placement="top"
                icon={<QuestionCircleOutlined />}
              >
                <Button htmlType="reset" className="m-2">
                  <StopOutlined /> {t("global.cancel")}
                </Button>
              </Popconfirm>
            </div>
            <div className="my-3">
              <Button
                className="btn-primary m-2"
                htmlType="submit"
                loading={buttonLoading}
              >
                <SaveOutlined /> {t("global.save")}
              </Button>
              <Button
                className="btn-success m-2"
                onClick={handlePublish}
                loading={buttonLoading}
              >
                <SendOutlined /> {t("global.publish")}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <Divider orientation="left">
        <h3 className="text-secondary mb-0">{t("news_theme.infos")}</h3>
      </Divider>
      <NewsThemeInputFormSection createMode={true} />
      <Divider orientation="left">
        <h3 className="text-secondary mb-0">{t("news_theme.color")}</h3>
      </Divider>
      <NewsThemeColorFormSection color={color} setColor={setColor} />
      <Divider orientation="left">
        <h3 className="text-secondary mb-0">{t("news_theme.contact.title")}</h3>
      </Divider>
      <NewsThemeContactFormSection />
      <NewsThemeLinksFormSection />
    </Form>
  );
};

export default AddNewsThemeForm;
